import React, { useState, useEffect } from 'react';
import { FormikProps } from 'formik';
import { Check } from '@mui/icons-material';
import { useTranslation } from '@hooks/useTranslation';

interface RegistrationWizardProps {
  formik: FormikProps<any>;
  children: React.ReactNode[];
  stepTitles: string[];
  onStepChange?: (step: number) => void;
  /**
   * Gate for "Next" / final submit. The wizard has no per-step validation of its own —
   * it just increments — so without this a user can page straight past an unchosen plan
   * or an empty card form and only discover the problem on submit.
   */
  canAdvance?: (step: number) => boolean;
  /** Label for the final button. Trial wording is wrong for a plan billed immediately. */
  submitLabel?: string;
}

const RegistrationWizard: React.FC<RegistrationWizardProps> = ({
  formik,
  children,
  stepTitles,
  onStepChange,
  canAdvance,
  submitLabel,
}) => {
  // Sprint 28, story-143. Bucket 1 (pre-auth) — see docs/architecture.md "## Sprint 28" Decision 4.
  const tLogin = useTranslation('login');
  const tCommon = useTranslation('common');
  const resolvedSubmitLabel = submitLabel ?? tLogin.createAccount;
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset isSubmitting when formik.isSubmitting changes to false (error or success)
  useEffect(() => {
    if (!formik.isSubmitting && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [formik.isSubmitting, isSubmitting]);

  const stepAllowsAdvance = canAdvance ? canAdvance(currentStep) : true;

  const goToNextStep = () => {
    if (!stepAllowsAdvance) return;
    if (currentStep < children.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      onStepChange?.(nextStep);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      onStepChange?.(prevStep);
    }
  };

  // The step list shrinks when the user switches from a card-required plan to a card-free
  // one. Without this the wizard would sit on a step index that no longer exists and
  // render nothing.
  useEffect(() => {
    if (currentStep > children.length - 1) {
      setCurrentStep(Math.max(0, children.length - 1));
    }
  }, [children.length, currentStep]);

  const handleFinalSubmit = () => {
    if (!stepAllowsAdvance) return;
    setIsSubmitting(true);
    formik.handleSubmit();
  };

  const isStepComplete = (stepIndex: number) => {
    return stepIndex < currentStep;
  };

  return (
    <div className="w-full">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {stepTitles.map((title, index) => (
            <React.Fragment key={index}>
              <div className="flex flex-col items-center flex-1">
                {/* Step Circle */}
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
                    isStepComplete(index)
                      ? 'bg-indigo-600 border-indigo-600 text-white'
                      : index === currentStep
                      ? 'bg-white dark:bg-gray-800 border-indigo-600 text-indigo-600'
                      : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400'
                  }`}
                >
                  {isStepComplete(index) ? (
                    <Check fontSize="small" />
                  ) : (
                    <span className="text-sm font-semibold">{index + 1}</span>
                  )}
                </div>
                {/* Step Title */}
                <span
                  className={`mt-2 text-xs font-medium text-center ${
                    index === currentStep
                      ? 'text-indigo-600 dark:text-indigo-400'
                      : isStepComplete(index)
                      ? 'text-gray-700 dark:text-gray-300'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}
                >
                  {title}
                </span>
              </div>

              {/* Connector Line */}
              {index < stepTitles.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 transition-all ${
                    isStepComplete(index)
                      ? 'bg-indigo-600'
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                  style={{ marginBottom: '2rem' }}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="mb-6">{children[currentStep]}</div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={goToPreviousStep}
          disabled={currentStep === 0}
          className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
            currentStep === 0
              ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          {tCommon.previous}
        </button>

        {currentStep < children.length - 1 ? (
          <button
            type="button"
            onClick={goToNextStep}
            disabled={!stepAllowsAdvance}
            className="px-6 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors disabled:bg-indigo-400 disabled:cursor-not-allowed"
          >
            {tCommon.next}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleFinalSubmit}
            disabled={isSubmitting || formik.isSubmitting || !stepAllowsAdvance}
            className="px-6 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors disabled:bg-indigo-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {(isSubmitting || formik.isSubmitting) && (
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {isSubmitting || formik.isSubmitting ? tLogin.creatingAccount : resolvedSubmitLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default RegistrationWizard;
